<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { useVuelidate } from '@vuelidate/core'
	import '@vuepic/vue-datepicker/dist/main.css'
	import { watch } from 'vue'
	import { useRouter } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import BreadCrumb from '@/components/BreadCrumb.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompanyUser from '@/modules/companies/composables/useCompanyUser'
	import UserForm from '@/modules/users/components/UserForm.vue'

	import { assignCostCenter } from '../composables/useAssignCostCenter'
	import useUser from '../composables/useUser'
	import useUsersCompany from '../composables/useUsersCompany'

	const { push } = useRouter()
	const toast = useToast()

	const queryClient = useQueryClient()
	const { user: userLogged } = useAuthUser()

	const { createForm, resetCreateForm, rulesUserValidation } = useUser({
		immediate: false,
	})

	const { abilities, redirectTo404 } = useHasPermissionUser()

	const { createUserMutationAsync } = useCompanyUser()

	const { refetch } = useUsersCompany(
		{},
		{
			immediate: false,
		}
	)

	const pages = [
		{ name: 'Usuarios', to: { name: 'users.index' }, current: true },
		{ name: 'Crear usuario', to: { name: 'users.create' } },
	]

	const visitUserList = () => {
		push({ name: 'users.index' })
	}

	const v$ = useVuelidate(rulesUserValidation, createForm)

	const handleSubmit = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		const user = await createUserMutationAsync({
			name: createForm.value.name,
			secondName: createForm.value.secondName ?? '',
			lastname: createForm.value.lastname,
			secondLastname: createForm.value.secondLastname ?? '',
			birthDate: createForm.value.birthDate,
			gender: createForm.value.gender,
			companyId: createForm.value.companyId || userLogged.value?.companyId!,
			roleId: createForm.value.roleId ?? '',
			email: createForm.value.email,
			companyGroupId: Number(createForm.value.companyGroupId) ?? undefined,
			jobTitle: '',
			haveAccess: createForm.value.haveAccess,
			departmentId: createForm.value.departmentId ?? 0,
			supervisorId: createForm.value.supervisorId ?? null,
		})

		if (user) {
			// El centro de costo va en una segunda llamada: el POST de alta no lo
			// acepta (ver useAssignCostCenter). Si falla, el usuario YA existe —
			// avisar y seguir, nunca presentar el alta como fallida.
			if (createForm.value.costCenterId) {
				try {
					await assignCostCenter({
						userId: user.id,
						departmentId: createForm.value.departmentId,
						costCenterId: createForm.value.costCenterId,
					})
				} catch {
					toast.warning(
						'El usuario se creó, pero no se pudo guardar su centro de costo. Asígnalo desde la edición del usuario.'
					)
				}
			}

			toast.success('Usuario creado correctamente')

			refetch()

			visitUserList()
			resetCreateForm()

			await queryClient.invalidateQueries(['usersCompany'])

			return
		}
	}

	watch(
		abilities,
		() => {
			redirectTo404('create-users', 'App\\Models\\Company')
		},
		{
			immediate: true,
		}
	)
</script>

<template>
	<BreadCrumb :pages="pages"></BreadCrumb>

	<section class="mx-auto my-8 grid h-full max-w-4xl gap-4 px-4">
		<UserForm
			@cancel-form="visitUserList"
			@send-form="handleSubmit"
			:not-have-default-value-role="true"
			:corporation-user="userLogged?.corporationId"
		/>
	</section>
</template>
